import { h, proxyCustomElement, HTMLElement, createEvent, Fragment, Host } from '@stencil/core/internal/client';
import { d as debounce } from './debounce.js';
import { f as forwardEvent, s as slotFilled } from './index3.js';
import { s as setInputRef, g as getError, a as getValidityObject, c as checkValidity, r as reportValidity, b as setCustomValidity, w as watchError, d as watchWarning, e as getHostAttributes, f as getFormFieldAttributes } from './index7.js';
import { i as isOtherOption } from './index6.js';
import { a as toValue, t as toLabel } from './index5.js';
import { i as isLavOption } from './types.js';
import { g as getBoxAttributes } from './util.js';
import { d as defineCustomElement$g } from './box.js';
import { d as defineCustomElement$f } from './check-input.js';
import { d as defineCustomElement$e } from './close-button.js';
import { d as defineCustomElement$d } from './divider.js';
import { d as defineCustomElement$c } from './form-field.js';
import { d as defineCustomElement$b } from './icon.js';
import { d as defineCustomElement$a } from './list.js';
import { d as defineCustomElement$9 } from './list-item.js';
import { d as defineCustomElement$8 } from './list-item-divider.js';
import { d as defineCustomElement$7 } from './list-item-header.js';
import { d as defineCustomElement$6 } from './list-item-meta.js';
import { d as defineCustomElement$5 } from './loader.js';
import { d as defineCustomElement$4 } from './menu-pane.js';
import { d as defineCustomElement$3 } from './status.js';
import { d as defineCustomElement$2 } from './tooltip.js';
import { d as defineCustomElement$1 } from './tooltip-label.js';

function HiddenHTMLDataList({ id, options, value, ref, }) {
    return (h("datalist", { ref: ref, hidden: true, id: id }, options?.map((option) => {
        if (!isLavOption(option)) {
            return null;
        }
        const optionValue = toValue(option);
        const optionLabel = toLabel(option);
        const isOptionChecked = (value || []).includes(`${optionValue}`);
        const isOptionDisabled = typeof option === 'object' && option.disabled;
        return (h("option", { key: optionLabel, selected: isOptionChecked, disabled: isOptionDisabled, value: optionValue }, optionLabel));
    })));
}

const inputCss = ":host{display:flex;position:relative;width:100%}:host .box{align-items:center;background-color:var(--jw-theme-form-field-default-bg-color);border:1px solid var(--jw-theme-form-border-regular-default-border-color);border-radius:.25rem;color:var(--jw-theme-form-field-filled-color);display:flex;font-family:Open Sans;font-size:.875rem;font-size:inherit;font-weight:400;font-weight:inherit;gap:.5rem;height:2rem;letter-spacing:-.0004em;line-height:1.25rem;min-width:64px;padding:0 .5rem;transition:border-color .3s ease;width:100%}:host .box[disabled]{background-color:var(--jw-theme-form-field-disabled-bg-color);color:var(--jw-theme-form-field-disabled-color);cursor:not-allowed}:host .box:not([disabled]):hover{border-color:var(--jw-theme-form-border-regular-hover-border-color)}:host .box:not([disabled]):focus,:host .box:not([disabled]):focus-within{border-color:var(--jw-theme-form-border-regular-focus-border-color);outline:var(--jw-theme-effects-focus-default-outline)}:host:focus,:host:focus-visible{outline:none}.inner{display:flex;width:100%}:host([is-empty]) .box{color:var(--jw-theme-form-field-placeholder-color)}:host([error-type=error]) .box{border-color:var(--jw-theme-form-border-error-default-border-color)}:host([error-type=error]:not([disabled]):hover) .box{border-color:var(--jw-theme-form-border-error-hover-border-color)}:host([error-type=warning]) .box{border-color:var(--jw-theme-form-border-warning-default-border-color)}:host([error-type=warning]:not([disabled]):hover) .box{border-color:var(--jw-theme-form-border-warning-hover-border-color)}.addon,::slotted(.addon){align-items:center;background-color:var(--jw-theme-form-addon-default-bg-color);color:var(--jw-theme-form-addon-default-color);cursor:inherit;display:inline-flex;flex:none;height:100%;padding:0 .5rem;white-space:nowrap}:host([addon-pos=start]) .box{padding-left:0}:host([addon-pos=end]) .box{padding-right:0}:host([addon-pos=start]) .addon,:host([addon-pos=start]) ::slotted(.addon){border-bottom-left-radius:.25rem;border-top-left-radius:.25rem}:host([addon-pos=end]) .addon,:host([addon-pos=end]) ::slotted(.addon){border-bottom-right-radius:.25rem;border-top-right-radius:.25rem}.prefix-icon{align-items:center;display:inline-flex}:host .prefix-icon wui-icon{color:var(--jw-theme-form-field-placeholder-color)}:host([disabled]) .prefix-icon wui-icon{color:var(--jw-theme-surface-text-disabled-color)}:host .input::placeholder{color:var(--jw-theme-form-field-placeholder-color)}:host .input::-ms-clear,:host .input::-webkit-clear-button,:host .input::-webkit-inner-spin-button,:host .input::-webkit-outer-spin-button{-webkit-appearance:none;appearance:none;display:none;margin:0}:host .input::-webkit-search-cancel-button,:host .input::-webkit-search-decoration,:host .input::-webkit-search-results-button,:host .input::-webkit-search-results-decoration{-webkit-appearance:none;appearance:none}:host .input::-webkit-calendar-picker-indicator{background:none;display:none}:host .input[type=number]{-webkit-appearance:textfield;appearance:textfield}:host .input::-webkit-inner-spin-button,:host .input::-webkit-outer-spin-button{-webkit-appearance:none;appearance:none;margin:0}:host .input:-webkit-autofill,:host .input:-webkit-autofill:first-line{font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem}:host .input:-webkit-autofill,:host .input:-webkit-autofill:focus,:host .input:-webkit-autofill:hover{box-shadow:0 0 0 1000px var(--jw-theme-form-field-default-bg-color) inset;-webkit-text-fill-color:var(--jw-theme-form-field-filled-color)}:host .input:-webkit-autofill[disabled]{box-shadow:0 0 0 1000px var(--jw-theme-form-field-disabled-bg-color) inset}:host .input[disabled]{cursor:not-allowed}:host .input[disabled]::placeholder{color:var(--jw-theme-surface-text-disabled-color)}:host .input{background:none;border:none;box-shadow:none;color:var(--jw-theme-form-field-filled-color);flex:1;float:left;font-family:Open Sans;font-size:.875rem;font-weight:400;height:100%;letter-spacing:-.0004em;line-height:1.25rem;margin:0;min-width:0;padding:0;width:100%}:host .input:focus,:host .input:focus-visible{outline:none}:host([is-empty]) .input{color:var(--jw-theme-form-field-placeholder-color)}:host([disabled]) .input{color:var(--jw-theme-form-field-disabled-color)}wui-loader{animation:fade-in .2s ease forwards}@keyframes fade-in{0%{opacity:0}to{opacity:1}}:host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host wui-loader.suggestions-loader{align-self:center}:host:host([width=sm]) div.box{width:8rem}:host:host([size=lg]) .box{font-size:15px;height:40px}";
const WuiInputStyle0 = inputCss;

const Input = /*@__PURE__*/ proxyCustomElement(class Input extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.choose = createEvent(this, "choose", 3);
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        this.setValue = (value) => {
            // Set value on input element and trigger event
            this.dirty = true;
            this.value = value;
            this.checkValidity();
            // Trigger event
            this.el.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        this.handleInput = (e) => {
            e.stopPropagation();
            const newValue = e.target.value;
            this.searchSuggestions(newValue);
            this.setValue(newValue);
        };
        this.handleClear = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!this.inputElm) {
                return;
            }
            // If already empty
            if (this.inputElm.value === '') {
                return;
            }
            this.setValue('');
        };
        this.searchSuggestions = (() => {
            let abortControllers = [];
            return debounce(async (query) => {
                if (!(this.suggestions && typeof this.suggestions === 'function')) {
                    return;
                }
                this.suggestionsLoading = true;
                // Cancel previous requests
                for (const c of abortControllers) {
                    c.abort();
                }
                // Create abort controller for this request and cache it internally
                const abortController = new AbortController();
                abortControllers.push(abortController);
                try {
                    this.suggestionsResolved = await this.suggestions(query, abortController);
                    // Automatically show suggestions after a search
                    if (query.trim()) {
                        this.suggestionsVisible = true;
                    }
                }
                catch (err) {
                    // Can't handle
                }
                finally {
                    this.suggestionsLoading = false;
                    // Remove this abortController from our internal state because we don't
                    // need it any more: the request is finished!
                    abortControllers = abortControllers.filter((a) => a !== abortController);
                }
            }, 150);
        })();
        this.toggleSuggestions = (state = !this.suggestionsVisible) => {
            if (state === this.suggestionsVisible) {
                return;
            }
            // trigger empty search when the user opens the menu
            if (state && this.suggestionsBehavior === 'greedy') {
                this.searchSuggestions('');
            }
            this.suggestionsVisible = state;
        };
        this.value = undefined;
        this.required = false;
        this.error = undefined;
        this.warning = undefined;
        this.validate = undefined;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.formField = undefined;
        this.type = 'text';
        this.placeholder = undefined;
        this.readonly = undefined;
        this.disabled = undefined;
        this.canClear = true;
        this.loading = undefined;
        this.size = 'md';
        this.width = undefined;
        this.addon = undefined;
        this.addonPosition = 'start';
        this.min = undefined;
        this.max = undefined;
        this.step = undefined;
        this.pattern = undefined;
        this.autocomplete = 'off';
        this.icon = undefined;
        this.suggestions = undefined;
        this.suggestionsAction = undefined;
        this.suggestionsBehavior = 'lazy';
        this.suggestionsLoading = false;
        this.suggestionsResolved = undefined;
        this.suggestionsVisible = false;
        this.minLength = undefined;
        this.maxLength = undefined;
        this.name = undefined;
        this.list = undefined;
        this.passwordVisible = false;
        this.form = undefined;
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    async handleKeyDown(e) {
        if (e.key === 'Enter') {
            const form = this.form
                ? document.querySelector(`form#${this.form}`)
                : this.el.closest('form');
            if (form) {
                e.preventDefault();
                form.requestSubmit();
            }
        }
        const wuiMenuPaneElement = this.el.shadowRoot?.querySelector('wui-menu-pane');
        if (wuiMenuPaneElement && (await wuiMenuPaneElement.handleKeyDown(e))) {
            // Event was handled by menu pane
            this.suggestionsVisible = !!wuiMenuPaneElement.open; // sync state
            return;
        }
        const wuiListElement = this.el.shadowRoot?.querySelector('wui-list');
        if (wuiListElement && (await wuiListElement.handleKeyDown(e))) {
            // Event was handled by list
            return;
        }
    }
    handleSelect(option) {
        const newValue = typeof option === 'object' ? option.value : option;
        this.choose.emit(newValue);
        this.setValue(newValue);
        this.toggleSuggestions(false);
    }
    focus() {
        this.inputElm?.focus();
    }
    renderSuggestions() {
        if (this.suggestions || this.suggestionsLoading) {
            return (h("wui-menu-pane", { open: this.suggestionsVisible || this.suggestionsLoading, stretch: "fit", placement: "bottom", onClickOutside: () => {
                    this.toggleSuggestions(false);
                }, onOpenChanged: (e) => {
                    // Sync state
                    this.toggleSuggestions(e.detail);
                } }, h("wui-list", { scrollSize: "md" }, this.suggestionsResolved?.length ? (this.suggestionsResolved.map((option, idx) => {
                if (isOtherOption(option)) {
                    switch (option.type) {
                        case 'header':
                            return h("wui-list-item-header", { key: idx, ...option });
                        case 'divider':
                            return h("wui-list-item-divider", { key: idx });
                        case 'meta':
                            return h("wui-list-item-meta", { key: idx, ...option });
                    }
                }
                return (h("wui-list-item", {
                    // ❓ We shouldn't use an array index for unique key, but
                    // for some reason, if we use the unique value of `option`,
                    // the whole rendering gets messed up during list keyboard
                    // navigation. No idea what's going on there...
                    key: idx, label: typeof option === 'object' ? option.value : option, description: typeof option === 'object' ? option.description : undefined, disabled: typeof option === 'object' ? option.disabled : false, onClick: () => this.handleSelect(option)
                }));
            })) : (h("wui-list-item-meta", { label: "No results", loading: this.suggestionsLoading })), (() => {
                if (!this.suggestionsAction) {
                    return;
                }
                if (!this.value) {
                    return;
                }
                const suggestionMatchesValue = this.suggestionsResolved?.some((s) => {
                    if (isOtherOption(s)) {
                        return false;
                    }
                    return (typeof s === 'object' ? s.value : s) === this.value;
                });
                if (suggestionMatchesValue) {
                    return;
                }
                return (h(Fragment, null, h("wui-list-item-divider", null), h("wui-list-item", { variant: "button", label: [
                        this.suggestionsAction.action,
                        ' "',
                        [this.value, 'bold'],
                        '"',
                        this.suggestionsAction.type ? ` ${this.suggestionsAction.type}` : '',
                    ], icon: this.suggestionsAction.icon ?? 'plus', onClick: () => {
                        this.toggleSuggestions(false);
                        if (!this.value) {
                            return;
                        }
                        this.suggestionsAction?.onSelect(this.value);
                    } })));
            })())));
        }
    }
    renderInner() {
        const loading = this.loading || this.suggestionsLoading;
        let icon = this.icon;
        if (this.type === 'search') {
            icon = 'search';
        }
        return (h(Fragment, null, this.suggestions && (h(HiddenHTMLDataList, { id: this.name, options: this.suggestionsResolved, value: this.value })), h("div", { class: "box", ...getBoxAttributes(this) }, h("slot", { name: "custom-addon" }), this.addon && this.addonPosition === 'start' && typeof this.addon === 'string' && (h("span", { class: "addon" }, this.addon)), icon && (h("div", { class: "prefix-icon" }, h("wui-icon", { name: icon, size: this.size === 'lg' ? 'lg' : 'md' }))), h("input", { type: this.type === 'password' && this.passwordVisible ? 'text' : this.type, class: "input", ref: this.setInputRef, onInput: this.handleInput, onChange: (e) => {
                // Don't use the change event, use `input`
                e.stopPropagation();
            }, onKeyDown: (e) => {
                this.handleKeyDown(e);
            }, placeholder: this.placeholder, autocomplete: this.autocomplete, disabled: this.disabled, readonly: this.readonly, min: this.min, max: this.max, step: this.step, name: this.name, list: this.list, minLength: this.minLength, maxLength: this.maxLength, required: !!this.required, pattern: this.pattern, onFocus: (e) => {
                forwardEvent(e, this.el);
            }, onBlur: (e) => {
                forwardEvent(e, this.el);
            },
            // Value has to be set last
            value: this.value }), h("slot", null), (() => {
            // Allow toggling password visibility
            if (this.value && this.type === 'password') {
                return (h("wui-icon", { name: this.passwordVisible ? 'eye' : 'eye-off', role: "button", onClick: () => {
                        this.passwordVisible = !this.passwordVisible;
                    } }));
            }
            // Show loader
            if (loading) {
                return h("wui-loader", { size: "sm" });
            }
            // Allow showing suggestions
            if (this.suggestions && this.suggestionsBehavior === 'greedy') {
                return (h("wui-icon", { name: "chevron-down", role: "button", onClick: () => {
                        this.toggleSuggestions();
                    } }));
            }
            // Allow clearing input
            if (this.canClear && !this.disabled && !this.readonly && this.value) {
                return h("wui-close-button", { tooltip: "Clear", size: "sm", onClick: this.handleClear });
            }
        })(), this.addon && this.addonPosition === 'end' && typeof this.addon === 'string' && (h("span", { class: "addon" }, this.addon))), this.renderSuggestions()));
    }
    render() {
        return (h(Host, { key: '9427640cdfbbba9fbb807289bc07d1ab578fa72f', ...getHostAttributes(this), tabIndex: 0, "addon-pos": this.addon || slotFilled(this.el, 'custom-addon') ? this.addonPosition : undefined }, h("wui-form-field", { key: '21cdcdc3c214b5c26e9882f062c680d978f38597', ...getFormFieldAttributes(this) }, this.renderInner())));
    }
    get el() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return WuiInputStyle0; }
}, [1, "wui-input", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16],
        "reportValidity": [16],
        "setCustomValidity": [16],
        "formField": [16],
        "type": [513],
        "placeholder": [1],
        "readonly": [516],
        "disabled": [516],
        "canClear": [4, "can-clear"],
        "loading": [516],
        "size": [513],
        "width": [513],
        "addon": [1],
        "addonPosition": [1, "addon-position"],
        "min": [8],
        "max": [8],
        "step": [2],
        "pattern": [1],
        "autocomplete": [1],
        "icon": [1],
        "suggestions": [16],
        "suggestionsAction": [16],
        "suggestionsBehavior": [1, "suggestions-behavior"],
        "minLength": [2, "min-length"],
        "maxLength": [2, "max-length"],
        "name": [1],
        "list": [1],
        "form": [1],
        "customValidation": [32],
        "showValidity": [32],
        "suggestionsLoading": [32],
        "suggestionsResolved": [32],
        "suggestionsVisible": [32],
        "passwordVisible": [32]
    }, undefined, {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-input", "wui-box", "wui-check-input", "wui-close-button", "wui-divider", "wui-form-field", "wui-icon", "wui-list", "wui-list-item", "wui-list-item-divider", "wui-list-item-header", "wui-list-item-meta", "wui-loader", "wui-menu-pane", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, Input);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-list-item-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-list-item-meta":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { HiddenHTMLDataList as H, Input as I, defineCustomElement as d };

//# sourceMappingURL=input.js.map